import fileSaver from 'file-saver';
import httpClient from '~/lib/http-client';

const { saveAs } = fileSaver;

/** Matches `TURN_IN_FLIGHT_CODE` on the server (`export.controller.ts`). */
export const TURN_IN_FLIGHT_CODE = 'TURN_IN_FLIGHT';

/**
 * Download this chat's code as a zip and hand it to the browser.
 *
 * The archive is built server-side (the workspace tar, or the GitHub snapshot
 * when the pod is asleep) so the download is the real project — every file,
 * binaries included — not the subset the editor's file watcher happens to hold.
 *
 * Paid plans only. A free user gets a 403 tagged `PLAN_REQUIRED`, which callers
 * hand to `handlePlanRequired` to open the plans dialog.
 */
export async function apiDownloadCode(chatId: string): Promise<void> {
  try {
    const res = await httpClient.get<Blob>(`/api/export/${chatId}/zip`, { responseType: 'blob' });

    saveAs(res.data, filenameFrom(res.headers['content-disposition']) ?? 'project.zip');
  } catch (err) {
    throw await withDecodedError(err);
  }
}

/**
 * `responseType: 'blob'` applies to error responses too, so a 403's JSON body
 * arrives as a Blob and `err.response.data.code` reads undefined — which would
 * turn every gate refusal into a generic "download failed" toast. Decode it back
 * into an object so the shared `PLAN_REQUIRED` / `TURN_IN_FLIGHT` handling works
 * exactly as it does for ordinary JSON calls.
 */
async function withDecodedError(err: any): Promise<any> {
  const body = err?.response?.data;

  if (!(body instanceof Blob)) {
    return err;
  }

  try {
    err.response.data = JSON.parse(await body.text());
  } catch {
    // Not JSON (a proxy's HTML error page, an empty body) — leave it alone.
  }

  return err;
}

/** `attachment; filename="kelvin-watches.zip"` → `kelvin-watches.zip`. */
function filenameFrom(header: unknown): string | null {
  if (typeof header !== 'string') {
    return null;
  }

  return /filename="?([^"]+)"?/.exec(header)?.[1] ?? null;
}
